import { useCallback, useMemo, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';

import { getCreateServiceErrorMessage } from './getCreateServiceErrorMessage';
import {
  buildServicePayload,
  performInteractionResubmit,
  performSubmit,
  SubmitCreateServiceArgs,
} from './submitCreateService';
import { CreateServiceFormValues } from './types';
import { ClientHierarchySelection } from './useClientHierarchy';
import { UseCreateServiceInteractionsResult } from './useCreateServiceInteractions';
import { LocationSearchState } from './useLocationSearch';
import { ServiceObject } from '../../../@types';
import { useAddService } from '../../../queries/service-query';

export interface UseCreateServiceSubmitArgs {
  values: CreateServiceFormValues;
  hierarchy: ClientHierarchySelection;
  location: LocationSearchState;
  submittedFaultId: string;
  isFormValid: boolean;
  addService: ReturnType<typeof useAddService>;
  navigate: NavigateFunction;
  interactions: UseCreateServiceInteractionsResult;
  onSuccess: () => void;
}

export interface UseCreateServiceSubmitResult {
  submitArgs: SubmitCreateServiceArgs;
  serviceProxy: ServiceObject;
  onSubmit: () => Promise<void>;
  submitError: string;
  clearSubmitError: () => void;
}

export const useCreateServiceSubmit = (
  args: UseCreateServiceSubmitArgs
): UseCreateServiceSubmitResult => {
  const { values, hierarchy, location, submittedFaultId, isFormValid } = args;
  const { addService, navigate, interactions, onSuccess } = args;
  const [submitError, setSubmitError] = useState('');

  // Legacy FormInterceptor fragments used for exotic field types (Signature,
  // FaultSelection, ModifyStructure, etc.) expect a ServiceObject. We don't
  // have a created service yet at render time, so use the same payload shape
  // buildServicePayload produces for submit — it carries the ids and codes the
  // fragments actually read.
  const submitArgs: SubmitCreateServiceArgs = useMemo(
    () => ({
      values,
      clientId: hierarchy.clientId,
      contractId: hierarchy.contractId,
      brandId: hierarchy.brandId,
      parentId: hierarchy.parentId,
      brandSelectionId: hierarchy.brandSelectionId,
      mainLocationNode: location.mainLocationNode,
      subLocationNode: location.subLocationNode,
      reportedFaultId: submittedFaultId,
    }),
    [values, hierarchy, location, submittedFaultId]
  );

  const serviceProxy = useMemo<ServiceObject>(
    () => buildServicePayload(submitArgs) as unknown as ServiceObject,
    [submitArgs]
  );

  const onSubmit = useCallback(async () => {
    setSubmitError('');
    if (!isFormValid) return;
    try {
      if (interactions.hasForms) {
        const prep = interactions.prepareSubmission();
        if (!prep.ok) {
          setSubmitError(
            `${prep.missingCount} interaction field${prep.missingCount === 1 ? '' : 's'} still need a value before this service can be created.`
          );
          return;
        }
        const outcome = await performInteractionResubmit(
          { originalPayload: prep.originalPayload, forms: prep.forms },
          { addService, navigate, onSuccessMessage: onSuccess }
        );
        if (outcome.ok) interactions.reset();
        else if (outcome.serviceId) interactions.setServiceId(outcome.serviceId);
        return;
      }
      const outcome = await performSubmit(submitArgs, {
        addService,
        navigate,
        onSuccessMessage: onSuccess,
      });
      // First submit may have created the service even when interactions
      // remain — capture the id so file/photo uploads on the returned forms
      // know which service to attach to.
      if (outcome.serviceId) interactions.setServiceId(outcome.serviceId);
    } catch (err) {
      setSubmitError(getCreateServiceErrorMessage(err));
    }
  }, [isFormValid, interactions, submitArgs, addService, navigate, onSuccess]);

  return {
    submitArgs,
    serviceProxy,
    onSubmit,
    submitError,
    clearSubmitError: () => setSubmitError(''),
  };
};
